/**
 * The Plot tab's signal pool, across one or many ships.
 *
 * Two things make this more than a concat. Ships do not share a schema - P-ST
 * carries DG1..DG5 where A-CS stops at DG4 - so the pool is a *union* and each
 * entry has to say how many of the selected ships can actually draw it. And
 * every ship also carries columns that exist but never report: on P-ST, 13 of
 * 294 columns are 100% null (`Humidity`, `gt_load`, the shore-power feeds).
 * Those are sensors the ship was never fitted with, and offering them means
 * plotting an empty line.
 *
 * So the rules, in order:
 *
 *  1. Union the columns of every plotted ship.
 *  2. Drop a column that is 100% null on *every* ship that has it.
 *  3. Coverage counts the ships where the column exists **and has data** -
 *     counting mere existence would make "2 of 3" lie when one of the two never
 *     reports.
 *
 * Derived signals are formulas, not stored columns, so they are always offered;
 * their coverage is the set of ships carrying every column they reference.
 *
 * This is the Plot tab only. The Export column selector keeps showing
 * everything with its existing null-% badge - knowing a column is empty is
 * often the point when you are extracting data.
 */

import { getColumns } from '../../api/endpoints.ts';
import { DEFAULT_SOURCE } from '../../state/data-sources.ts';
import {
  activeDataSource,
  allColumns,
  derivedSignals,
  plottedShipIds,
  selectedShip,
  type DerivedSignal,
} from '../../state/store.ts';
import { derivedAsColumns } from '../derived/derived-state.ts';
import { shipLabels } from '../../utils/ships.ts';
import type { ColumnInfo } from '../../api/types.ts';

/** How many of the plotted ships can draw a signal. */
export interface ShipCoverage {
  /** Ships that have the column with at least one non-null sample. */
  ships: string[];
  /** Ships plotted in total. */
  total: number;
}

/** A pool entry: a column plus, when several ships are plotted, its coverage. */
export interface PoolColumn extends ColumnInfo {
  /** Absent while a single ship is plotted - there is nothing to compare. */
  coverage?: ShipCoverage;
}

/**
 * Per-ship column lists, keyed by data source and ship id. The anchor ship's
 * entry mirrors `allColumns` - for Explore's source only; comparison ships are
 * fetched on demand and kept until the page reloads, since a ship's schema
 * only changes when new months land.
 *
 * The source is part of the key because the same ship has different columns
 * in each (EOS names vs raw ClickHouse names on Live): a dashboard tile reading
 * EOS while Explore is on Live must not be checked against Live's columns -
 * every signal would look missing and the tile would draw nothing.
 */
const cache = new Map<string, ColumnInfo[]>();
const keyOf = (shipId: string, source: string): string => `${source || DEFAULT_SOURCE}\u0000${shipId}`;

/** True when the column reports at least one sample for that ship. */
function hasData(col: ColumnInfo): boolean {
  // total_count is 0 on legacy metadata that predates the null-detail scan;
  // treat that as "unknown, assume usable" rather than hiding a real signal.
  if (!col.total_count) return true;
  return Number(col.null_pct) < 100;
}

/** The column names a derived signal reads (constants and blanks excluded). */
export function derivedOperands(d: DerivedSignal): string[] {
  if (d.expr && d.expr.trim()) {
    // Free-form expressions: pull bare identifiers and let unknown ones fall
    // through as "not available", same as the backend's validator does.
    return [...d.expr.matchAll(/[A-Za-z_][A-Za-z0-9_.]*/g)].map((m) => m[0]);
  }
  return [d.operand_a, d.operand_b_col].filter((c): c is string => !!c);
}

export function cacheShipColumns(shipId: string, cols: ColumnInfo[], source = DEFAULT_SOURCE): void {
  cache.set(keyOf(shipId, source), cols);
}

export function cachedShipColumns(shipId: string, source = DEFAULT_SOURCE): ColumnInfo[] | undefined {
  return cache.get(keyOf(shipId, source));
}

export function clearShipColumnCache(): void {
  cache.clear();
}

/**
 * Fetch and cache the columns of every comparison ship that isn't cached yet.
 * The anchor ship is skipped - `loadColumns` already put it in `allColumns`.
 * Failures are swallowed per ship: a ship whose schema won't load simply
 * contributes nothing to the pool rather than emptying the picker.
 */
export async function ensureShipColumns(shipIds: string[]): Promise<void> {
  const anchor = selectedShip.value;
  const source = activeDataSource.value;
  const missing = shipIds.filter((id) => id !== anchor && !cache.has(keyOf(id, source)));
  if (!missing.length) return;
  await Promise.all(
    missing.map(async (id) => {
      try {
        cache.set(keyOf(id, source), await getColumns(id, source));
      } catch {
        cache.set(keyOf(id, source), []);
      }
    }),
  );
}

/**
 * Columns known for a ship on a source: Explore's `allColumns` for its own
 * anchor ship and source, the cache otherwise (empty when never fetched).
 */
function columnsFor(shipId: string, source: string = activeDataSource.value): ColumnInfo[] {
  if (shipId === selectedShip.value && (source || DEFAULT_SOURCE) === (activeDataSource.value || DEFAULT_SOURCE)) {
    return allColumns.value;
  }
  return cache.get(keyOf(shipId, source)) ?? [];
}

/**
 * Build the pool from explicit inputs. Kept pure and separate from the atoms so
 * the union/coverage/empty rules can be tested without a store.
 */
export function buildPool(
  shipIds: string[],
  columnsByShip: Map<string, ColumnInfo[]>,
  derived: DerivedSignal[] = [],
): PoolColumn[] {
  const multi = shipIds.length > 1;
  /** name → ships that have it at all, and ships where it has data. */
  const present = new Map<string, { any: ColumnInfo[]; live: string[] }>();

  for (const ship of shipIds) {
    for (const col of columnsByShip.get(ship) ?? []) {
      let entry = present.get(col.name);
      if (!entry) {
        entry = { any: [], live: [] };
        present.set(col.name, entry);
      }
      entry.any.push(col);
      if (hasData(col)) entry.live.push(ship);
    }
  }

  const out: PoolColumn[] = [];
  for (const [name, entry] of present) {
    // Rule 2: no ship that has this column ever reports a value for it.
    if (!entry.live.length) continue;

    // Types agree across ships in practice; prefer the anchor's reading, which
    // is `any[0]` because ships are walked anchor-first.
    const base = entry.any[0]!;
    const merged: PoolColumn = {
      ...base,
      null_count: entry.any.reduce((n, c) => n + (Number(c.null_count) || 0), 0),
      total_count: entry.any.reduce((n, c) => n + (Number(c.total_count) || 0), 0),
      months: [...new Set(entry.any.flatMap((c) => c.months ?? []))].sort(),
    };
    merged.null_pct = merged.total_count
      ? Math.round((merged.null_count / merged.total_count) * 100)
      : base.null_pct;
    if (multi) merged.coverage = { ships: entry.live, total: shipIds.length };
    out.push(merged);
  }

  // Derived signals are computed, so they are always offered. Their coverage is
  // the ships that carry every column the formula reads.
  const names = new Set(out.map((c) => c.name));
  for (const pseudo of derivedAsColumns(derived)) {
    const spec = derived.find((d) => d.name === pseudo.name)!;
    const operands = derivedOperands(spec).filter((o) => names.has(o) || present.has(o));
    const entry: PoolColumn = { ...pseudo };
    if (multi) {
      const ships = shipIds.filter((ship) => {
        const cols = columnsByShip.get(ship) ?? [];
        const live = new Set(cols.filter(hasData).map((c) => c.name));
        return operands.every((o) => live.has(o));
      });
      entry.coverage = { ships, total: shipIds.length };
    }
    out.push(entry);
  }

  return out;
}

/**
 * The pool for the currently plotted ships. Replaces `plottableColumns()` on
 * the Plot tab; single-ship behaviour is the old list minus signals that never
 * report, which were previously offered and plotted as empty lines.
 */
export function plotColumnPool(): PoolColumn[] {
  const shipIds = plottedShipIds();
  if (!shipIds.length) return [];
  const byShip = new Map(shipIds.map((id) => [id, columnsFor(id)]));
  return buildPool(shipIds, byShip, derivedSignals.value);
}

/**
 * Every signal `shipId` can actually draw: its own columns that report data,
 * plus the derived signals whose operands it all carries.
 *
 * Used to scope each ship's request in a multi-ship fetch. Without it, one
 * ship-specific signal in the Y list (say `DG5_Power`, which P-ST has and A-CS
 * does not) makes the backend reject A-CS's whole request as an unknown column,
 * and A-CS disappears from the chart along with the signals it *does* have.
 */
export function liveSignalsForShip(shipId: string, source: string = activeDataSource.value): Set<string> {
  const live = new Set<string>();
  for (const col of columnsFor(shipId, source)) if (hasData(col)) live.add(col.name);
  for (const d of derivedSignals.value) {
    if (derivedOperands(d).every((o) => live.has(o))) live.add(d.name);
  }
  return live;
}

/** `"2 of 3 ships"`, or null when every plotted ship draws this signal. */
export function coverageLabel(col: PoolColumn): string | null {
  const c = col.coverage;
  if (!c || c.ships.length === c.total) return null;
  return `${c.ships.length} of ${c.total} ships`;
}

/** Tooltip naming the ships that cannot draw a partially covered signal. */
export function coverageTitle(col: PoolColumn, shipIds: string[]): string | null {
  const c = col.coverage;
  if (!c || c.ships.length === c.total) return null;
  const have = new Set(c.ships);
  const missing = shipIds.filter((s) => !have.has(s));
  return `No data on ${shipLabels(missing)}`;
}
